/**
 * MessagingTriagePage.tsx — Triage de remitentes DM desconocidos.
 * Reemplaza la lista de 24 filas volcada en la descripción de una task
 * (unknownSenders.ts) por una pantalla dedicada: List Pattern + SelectionActionBar
 * de Leads/Tasks. Pre-marca (checkbox tildado) SOLO las filas con phishingFlag —
 * el nombre faltante/genérico ("Facebook user") no es evidencia de nada y nunca
 * pre-selecciona (corrección explícita de Eduardo). "Ignorar" es reversible via
 * la pestaña Ignorados + "Quitar de ignorados".
 */
import { useEffect, useState } from 'react';
import type { ChannelContactDecisionDm } from '@evo-hub/shared';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Icon } from '../components/Icon.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { Tabs } from '../components/ui/Tabs.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Pill } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { parseServerDate } from '../lib/date.js';
import { LinkToContactModal } from '../components/contacts/LinkToContactModal.js';
import { MessagingTriageClaudePanel } from '../components/messaging/MessagingTriageClaudePanel.js';
import {
  useTriage, useDismissChannels, useUndismissChannels,
  useLinkToContact, useCreateContactsFromTriage, useCreateLeadsFromTriage, useUnlinkChannels,
  type TriageRow, type TriageStatus,
} from '../hooks/useDmTriage.js';

const CREATED_DECISIONS = new Set<ChannelContactDecisionDm>(['contact_created', 'lead_created']);

const GRID = 'var(--evo-table-check-col) minmax(0, 1.2fr) minmax(0, 2.4fr) minmax(0, 0.6fr) minmax(0, 0.9fr)';

const GENERIC_NAMES = new Set(['facebook user', 'instagram user']);

function isUnidentified(name: string | null): boolean {
  if (!name) return true;
  return GENERIC_NAMES.has(name.trim().toLowerCase());
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const colStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...extra,
});
const headStyle: React.CSSProperties = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500, textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)',
};

export function MessagingTriagePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [tab, setTab] = useState<TriageStatus>('pending');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showLinkModal, setShowLinkModal] = useState(false);

  const { data, isLoading } = useTriage(tab);
  const dismiss = useDismissChannels();
  const undismiss = useUndismissChannels();
  const link = useLinkToContact();
  const createContacts = useCreateContactsFromTriage();
  const createLeads = useCreateLeadsFromTriage();
  const unlink = useUnlinkChannels();

  const rows = data?.rows ?? [];

  function toggleSelect(id: number) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function clearSelection() { setSelected(new Set()); }

  // Bug real: el toast de éxito se mostraba sin importar cuántas filas
  // realmente cambiaron — un WHERE que no matcheaba (ej. contactId ya seteado
  // por otra vía) devolvía count:0 y el usuario veía "listo" igual, sin
  // enterarse de que no pasó nada. Ahora se compara contra lo esperado.
  function warnIfIncomplete(actual: number, expected: number) {
    if (actual < expected) {
      toast.warning(t('dmTriage.bulk.incomplete', { actual, expected }));
    }
  }

  // Ninguna de las mutations de esta pantalla tenía onError — un 500/network
  // real quedaba mudo, el botón simplemente dejaba de cargar sin explicar
  // nada. Se pasa a cada .mutate() de abajo.
  function handleMutationError(err: unknown) {
    toast.error(err instanceof Error ? err.message : String(err));
  }

  // Al entrar a Pendientes, pre-tildar SOLO las filas con phishingFlag=true.
  // Nunca por nombre faltante/genérico — esa señal la decide Eduardo a mano.
  useEffect(() => {
    if (tab === 'pending' && data) {
      setSelected(new Set(rows.filter(r => r.phishingFlag).map(r => r.channelId)));
    } else {
      setSelected(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-tildar al cambiar de tab o al llegar data nueva
  }, [tab, data]);

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({ title: t('dmTriage.pageTitle'), heroIcon: 'messages', heroIconColor: 'var(--evo-steel)' });
    return () => setOverride({});
  }, [setOverride, t]);

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <MessagingTriageClaudePanel />
      <div style={{ padding: '0 var(--evo-content-pad-x)', borderBottom: '1px solid var(--evo-stroke)' }}>
        <Tabs<TriageStatus>
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'pending', label: t('dmTriage.tabs.pending') },
            { value: 'dismissed', label: t('dmTriage.tabs.dismissed') },
            { value: 'linked', label: t('dmTriage.tabs.linked') },
          ]}
        />
      </div>

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('dmTriage.selected', { count: selected.size })}
        onClearSelection={clearSelection}
      >
        {tab === 'pending' && (
          <>
            <SelectionActionBar.Action
              label={t('dmTriage.actions.link')}
              onClick={() => setShowLinkModal(true)}
            />
            <SelectionActionBar.Action
              label={t('dmTriage.actions.createContact')}
              loading={createContacts.isPending}
              confirmRequired
              onClick={() => createContacts.mutate(Array.from(selected), {
                onSuccess: r => { warnIfIncomplete(r.created.length, selected.size); clearSelection(); toast.info(t('dmTriage.bulk.contactsCreated', { count: r.created.length })); },
                onError: handleMutationError,
              })}
            />
            <SelectionActionBar.Action
              label={t('dmTriage.actions.createLead')}
              loading={createLeads.isPending}
              confirmRequired
              onClick={() => createLeads.mutate(Array.from(selected), {
                onSuccess: r => { warnIfIncomplete(r.created.length, selected.size); clearSelection(); toast.info(t('dmTriage.bulk.leadsCreated', { count: r.created.length })); },
                onError: handleMutationError,
              })}
            />
            <SelectionActionBar.Action
              label={t('dmTriage.actions.dismiss')}
              loading={dismiss.isPending}
              confirmRequired
              onClick={() => dismiss.mutate(Array.from(selected), {
                onSuccess: r => { warnIfIncomplete(r.dismissed, selected.size); clearSelection(); toast.info(t('dmTriage.bulk.dismissed', { count: r.dismissed })); },
                onError: handleMutationError,
              })}
            />
          </>
        )}
        {tab === 'dismissed' && (
          <SelectionActionBar.Action
            label={t('dmTriage.actions.undismiss')}
            loading={undismiss.isPending}
            onClick={() => undismiss.mutate(Array.from(selected), {
              onSuccess: r => { warnIfIncomplete(r.restored, selected.size); clearSelection(); toast.info(t('dmTriage.bulk.restored', { count: r.restored })); },
              onError: handleMutationError,
            })}
          />
        )}
        {tab === 'linked' && (
          <SelectionActionBar.Action
            label={t('dmTriage.actions.unlink')}
            loading={unlink.isPending}
            confirmRequired
            onClick={() => {
              const anyCreated = rows.some(r => selected.has(r.channelId) && CREATED_DECISIONS.has((r.contactDecision ?? '') as ChannelContactDecisionDm));
              unlink.mutate(Array.from(selected), {
                onSuccess: r => {
                  warnIfIncomplete(r.unlinked, selected.size);
                  clearSelection();
                  toast.info(t(anyCreated ? 'dmTriage.bulk.unlinkedKeepsCreated' : 'dmTriage.bulk.unlinked', { count: r.unlinked }));
                },
                onError: handleMutationError,
              });
            }}
          />
        )}
      </SelectionActionBar>

      {showLinkModal && (
        <LinkToContactModal
          count={selected.size}
          loading={link.isPending}
          onClose={() => setShowLinkModal(false)}
          onConfirm={contactId => link.mutate({ channelIds: Array.from(selected), contactId }, {
            onSuccess: r => {
              warnIfIncomplete(r.linked, selected.size);
              setShowLinkModal(false);
              clearSelection();
              toast.info(t('dmTriage.bulk.linked', { count: r.linked }));
            },
            onError: handleMutationError,
          })}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: GRID, height: 'var(--evo-toolbar-height)', alignItems: 'center', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, background: 'var(--evo-bg-2)' }}>
        <div style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
          <Checkbox
            checked={rows.length > 0 && rows.every(r => selected.has(r.channelId))}
            indeterminate={selected.size > 0 && !rows.every(r => selected.has(r.channelId))}
            onChange={() => {
              if (rows.every(r => selected.has(r.channelId))) rows.forEach(r => toggleSelect(r.channelId));
              else rows.filter(r => !selected.has(r.channelId)).forEach(r => toggleSelect(r.channelId));
            }}
          />
        </div>
        <div style={headStyle}>{t('dmTriage.columns.sender')}</div>
        <div style={headStyle}>{t('dmTriage.columns.lastMessage')}</div>
        <div style={headStyle}>{t('dmTriage.columns.platform')}</div>
        <div style={headStyle}>{t('dmTriage.columns.date')}</div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && rows.length === 0 && <EmptyState size="md" reserveSpace title={t(`dmTriage.empty.${tab}`)} />}
        {rows.map((row: TriageRow) => {
          const unidentified = isUnidentified(row.senderDisplayName);
          return (
            <div key={row.channelId} style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'flex-start', minHeight: 'var(--evo-list-row-min-height)' }}
              >
                <div style={{ padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)' }} onClick={e => { e.stopPropagation(); toggleSelect(row.channelId); }}>
                  <div style={{ pointerEvents: 'none' }}><Checkbox checked={selected.has(row.channelId)} onChange={() => {}} /></div>
                </div>
                <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px) var(--evo-table-cell-py) 0', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)', overflow: 'hidden' }}>
                  <span className="truncate" style={{ display: 'block', color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)' }}>
                    {row.senderDisplayName ?? t('dmTriage.unidentified')}
                  </span>
                  <div style={{ display: 'flex', gap: 'var(--evo-space-1)', flexWrap: 'wrap' }}>
                    {tab === 'linked' ? (
                      <>
                        {row.contactName && (
                          <Pill tone="cyan" size="xs">{t('dmTriage.columns.linkedTo')}: {row.contactName}</Pill>
                        )}
                        {row.contactDecision && (
                          <Pill tone="muted" size="xs">{t(`dmTriage.decision.${row.contactDecision}`, { defaultValue: row.contactDecision })}</Pill>
                        )}
                      </>
                    ) : (
                      <>
                        {unidentified && <Pill tone="muted" size="xs">{t('dmTriage.unidentified')}</Pill>}
                        {row.phishingFlag && <Pill tone="danger" size="xs">{t('dmTriage.phishingProbable')}</Pill>}
                      </>
                    )}
                  </div>
                </div>
                <div style={colStyle({ whiteSpace: 'normal', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', display: '-webkit-box' })}>
                  {row.lastMessageBody ? row.lastMessageBody.replace(/\s+/g, ' ').slice(0, 160) : '—'}
                </div>
                <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', display: 'flex' }}>
                  {row.platform === 'instagram'
                    ? <Icon name="social-instagram" size={16} />
                    : row.platform === 'facebook'
                      ? <Icon name="social-facebook" size={16} />
                      : <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)' }}>{row.platform ?? '—'}</span>}
                </div>
                <div style={colStyle()}>{fmt(row.lastMessageAt)}</div>
              </div>
              <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
